import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Download,
  FilterList,
  Refresh,
  Visibility,
  Receipt,
  Payment,
  AccountBalance,
  SwapHoriz,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Transaction, Refund, FeeStructure } from '../types';
import { useTranslation } from '../i18n';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const TransactionReports: React.FC = () => {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [fees, setFees] = useState<FeeStructure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });

  // Function to translate transaction types
  const translateTransactionType = (type: string) => {
    switch (type) {
      case 'disbursement':
        return t('transactionReports.disbursement');
      case 'repayment':
        return t('transactionReports.repayment');
      case 'fee':
        return t('transactionReports.fee');
      case 'refund':
        return t('transactionReports.refund');
      default:
        return type;
    }
  };

  // Function to translate transaction status
  const translateTransactionStatus = (status: string) => {
    switch (status) {
      case 'completed':
        return t('transactionReports.completed');
      case 'pending':
        return t('transactionReports.pending');
      default:
        return status;
    }
  };

  useEffect(() => {
    const fetchTransactionData = async () => {
      try {
        setLoading(true);
        // Mock data - replace with actual API call
        const mockTransactions: Transaction[] = [
          {
            id: 'T001',
            loanId: 'L001',
            type: 'disbursement',
            amount: 500000,
            timestamp: '2024-01-15T10:00:00Z',
            description: 'Loan disbursement',
            reference: 'DIS-001',
            status: 'completed',
            netAmount: 500000,
          },
          {
            id: 'T002',
            loanId: 'L001',
            type: 'repayment',
            amount: 50000,
            timestamp: '2024-02-15T14:30:00Z',
            description: 'Monthly repayment',
            reference: 'REP-001',
            status: 'completed',
            fees: 2500,
            netAmount: 47500,
          },
          {
            id: 'T003',
            loanId: 'L002',
            type: 'fee',
            amount: 15000,
            timestamp: '2024-02-16T09:15:00Z',
            description: 'Late payment fee',
            reference: 'FEE-001',
            status: 'completed',
            netAmount: 15000,
          },
          {
            id: 'T004',
            loanId: 'L003',
            type: 'refund',
            amount: 25000,
            timestamp: '2024-02-17T16:45:00Z',
            description: 'Processing fee refund',
            reference: 'REF-001',
            status: 'completed',
            netAmount: 25000,
          },
        ];

        const mockRefunds: Refund[] = [
          {
            id: 'R001',
            loanId: 'L003',
            amount: 25000,
            reason: 'Processing fee refund due to early approval',
            requestDate: '2024-02-15T10:00:00Z',
            processedDate: '2024-02-17T16:45:00Z',
            status: 'processed',
            processedBy: 'admin@bank.com',
            notes: 'Approved by manager',
          },
        ];

        const mockFees: FeeStructure[] = [
          {
            id: 'F001',
            feeType: 'processing',
            amount: 5000,
            percentage: 1,
            description: 'Loan processing fee',
            applicableTo: 'all',
            active: true,
          },
          {
            id: 'F002',
            feeType: 'late',
            amount: 2500,
            percentage: 5,
            description: 'Late payment penalty',
            applicableTo: 'all',
            active: true,
          },
        ];

        setTransactions(mockTransactions);
        setRefunds(mockRefunds);
        setFees(mockFees);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch transaction data');
        setLoading(false);
      }
    };

    fetchTransactionData();
  }, []);

  const filteredTransactions = transactions.filter(transaction => {
    if (filterType !== 'all' && transaction.type !== filterType) return false;
    const transactionDate = new Date(transaction.timestamp);
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    return transactionDate >= startDate && transactionDate <= endDate;
  });

  const transactionSummary = {
    totalDisbursements: filteredTransactions.filter(t => t.type === 'disbursement').reduce((sum, t) => sum + t.amount, 0),
    totalRepayments: filteredTransactions.filter(t => t.type === 'repayment').reduce((sum, t) => sum + t.amount, 0),
    totalFees: filteredTransactions.filter(t => t.type === 'fee').reduce((sum, t) => sum + t.amount, 0),
    totalRefunds: filteredTransactions.filter(t => t.type === 'refund').reduce((sum, t) => sum + t.amount, 0),
  };

  const chartData = [
    { type: 'Disbursements', amount: transactionSummary.totalDisbursements, color: '#4caf50' },
    { type: 'Repayments', amount: transactionSummary.totalRepayments, color: '#2196f3' },
    { type: 'Fees', amount: transactionSummary.totalFees, color: '#ff9800' },
    { type: 'Refunds', amount: transactionSummary.totalRefunds, color: '#f44336' },
  ];

  const monthlyData = [
    { month: 'Jan', disbursements: 180000000, repayments: 150000000, fees: 8000000, refunds: 2000000 },
    { month: 'Feb', disbursements: 175000000, repayments: 160000000, fees: 7500000, refunds: 1500000 },
    { month: 'Mar', disbursements: 190000000, repayments: 170000000, fees: 9000000, refunds: 3000000 },
  ];

  const exportToXLS = (data: any[], fileName: string) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const exportToPDF = (headers: string[][], body: any[][], fileName: string) => {
    const doc = new jsPDF();
    autoTable(doc, { head: headers, body });
    doc.save(`${fileName}.pdf`);
  };

  const handleExportTransactions = (format: 'xls' | 'pdf') => {
    const data = filteredTransactions.map(t => ({
      'Transaction ID': t.id,
      'Loan ID': t.loanId,
      'Type': t.type,
      'Amount': t.amount,
      'Date': new Date(t.timestamp).toLocaleDateString(),
      'Status': t.status,
      'Reference': t.reference,
    }));

    if (format === 'xls') {
      exportToXLS(data, 'transactions-report');
    } else {
      const head = [['Transaction ID', 'Loan ID', 'Type', 'Amount', 'Date', 'Status', 'Reference']];
      const body = data.map(d => Object.values(d));
      exportToPDF(head, body, 'transactions-report');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Transaction Reports
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{t('common.transactionType')}</InputLabel>
            <Select
              value={filterType}
              label={t('common.transactionType')}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="all">{t('common.allTypes')}</MenuItem>
              <MenuItem value="disbursement">{t('transactionReports.disbursements')}</MenuItem>
              <MenuItem value="repayment">{t('transactionReports.repayments')}</MenuItem>
              <MenuItem value="fee">{t('transactionReports.fees')}</MenuItem>
              <MenuItem value="refund">{t('transactionReports.refunds')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={t('common.startDate')}
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={t('common.endDate')}
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Paper>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  {t('transactionReports.totalDisbursements')}
                </Typography>
                <Typography variant="h5" component="div">
                  {(transactionSummary.totalDisbursements / 1000000).toFixed(1)}M ZiG
                </Typography>
              </Box>
              <AccountBalance color="primary" />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  {t('transactionReports.totalRepayments')}
                </Typography>
                <Typography variant="h5" component="div">
                  {(transactionSummary.totalRepayments / 1000000).toFixed(1)}M ZiG
                </Typography>
              </Box>
              <Payment color="success" />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  {t('transactionReports.totalFees')}
                </Typography>
                <Typography variant="h5" component="div">
                  {(transactionSummary.totalFees / 1000).toFixed(0)}K ZiG
                </Typography>
              </Box>
              <Receipt color="warning" />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  {t('transactionReports.totalRefunds')}
                </Typography>
                <Typography variant="h5" component="div">
                  {(transactionSummary.totalRefunds / 1000).toFixed(0)}K ZiG
                </Typography>
              </Box>
              <SwapHoriz color="info" />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Charts */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Paper sx={{ p: 2, flex: '1 1 400px', minWidth: '400px' }}>
          <Typography variant="h6" gutterBottom>
            {t('transactionReports.transactionDistribution')}
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ type, percent }) => `${translateTransactionType(type)} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="amount"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip />
            </PieChart>
          </ResponsiveContainer>
        </Paper>

        <Paper sx={{ p: 2, flex: '1 1 400px', minWidth: '400px' }}>
          <Typography variant="h6" gutterBottom>
            {t('transactionReports.monthlyTrends')}
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <RechartsTooltip />
              <Legend />
              <Line type="monotone" dataKey="disbursements" stroke="#4caf50" name={t('transactionReports.disbursements')} />
              <Line type="monotone" dataKey="repayments" stroke="#2196f3" name={t('transactionReports.repayments')} />
              <Line type="monotone" dataKey="fees" stroke="#ff9800" name={t('transactionReports.fees')} />
              <Line type="monotone" dataKey="refunds" stroke="#f44336" name={t('transactionReports.refunds')} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      {/* Transactions Table */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            {t('transactionReports.transactionDetails')} ({filteredTransactions.length} {t('common.transactions')})
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => handleExportTransactions('xls')}
            >
              {t('common.exportXLS')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => handleExportTransactions('pdf')}
            >
              {t('common.exportPDF')}
            </Button>
          </Box>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.transactionId')}</TableCell>
                <TableCell>{t('common.loanId')}</TableCell>
                <TableCell>{t('common.type')}</TableCell>
                <TableCell>{t('common.amount')}</TableCell>
                <TableCell>{t('common.date')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('common.reference')}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>{transaction.id}</TableCell>
                  <TableCell>{transaction.loanId}</TableCell>
                  <TableCell>
                    <Chip
                      label={translateTransactionType(transaction.type)}
                      color={
                        transaction.type === 'disbursement' ? 'primary' :
                        transaction.type === 'repayment' ? 'success' :
                        transaction.type === 'fee' ? 'warning' : 'info'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{transaction.amount.toLocaleString('en-ZW')} ZiG</TableCell>
                  <TableCell>{new Date(transaction.timestamp).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={translateTransactionStatus(transaction.status)}
                      color={transaction.status === 'completed' ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{transaction.reference}</TableCell>
                  <TableCell>
                    <Tooltip title={t('common.viewDetails')}>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Refunds Section */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          {t('transactionReports.refundRequests')} ({refunds.length})
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('transactionReports.refundId')}</TableCell>
                <TableCell>{t('common.loanId')}</TableCell>
                <TableCell>{t('common.amount')}</TableCell>
                <TableCell>{t('transactionReports.reason')}</TableCell>
                <TableCell>{t('transactionReports.requestDate')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('transactionReports.processedBy')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {refunds.map((refund) => (
                <TableRow key={refund.id}>
                  <TableCell>{refund.id}</TableCell>
                  <TableCell>{refund.loanId}</TableCell>
                  <TableCell>{refund.amount.toLocaleString('en-ZW')} ZiG</TableCell>
                  <TableCell>{refund.reason}</TableCell>
                  <TableCell>{new Date(refund.requestDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={refund.status === 'processed' ? t('transactionReports.processed') : refund.status}
                      color={refund.status === 'processed' ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{refund.processedBy || 'N/A'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TransactionReports;
